import { ArrowRightIcon, FunnelIcon, SparkleIcon } from '@phosphor-icons/react';

import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { useOfferLayout } from '@voll-intelligence/intelligence-ui-kit';

/** O atalho das tarifas mais baratas e o seu `Desfazer` — os dados, não o botão: quem
 *  desenha é a saia, porque o rótulo muda com a largura dela. */
export interface SkirtShortcut {
  /** Quantos trechos o atalho fecharia — é o número do rótulo. */
  readonly pending: number;
  readonly undoable: boolean;
  /** Lote em voo: o botão não abre duas vezes. */
  readonly busy: boolean;
  readonly onAccept: () => void;
  readonly onUndo: () => void;
}

/** A porta para a lista completa: o trecho em que a pessoa está e quem abre. */
export interface SkirtExplore {
  readonly active: number;
  readonly onOpen: (leg: number) => void;
}

/**
 * O par atalho/`Desfazer`: DOIS estados de UM lugar, não dois controles. É a única
 * tela onde a recomendação da VOLL pode ser cobrada, e por isso ela tem duas
 * travas — o rótulo carrega o NÚMERO (um botão genérico que resolve três compras é
 * um cheque em branco, e o número encolhe conforme a pessoa resolve trechos à mão)
 * e a saída também é de um clique (quem fecha três trechos de uma vez não pode
 * precisar reabrir três escadas para voltar).
 *
 * O `Desfazer` VENCE o atalho enquanto houver lote a desfazer, inclusive quando o
 * lote completou o roteiro — é justamente aí que ele é indispensável. Ele é
 * recuperável nos dois sentidos: desfazer traz o atalho de volta com o número
 * cheio, então nunca se perde o caminho de refazer.
 *
 * `Sparkle`, o glifo da escolha do agente — o mesmo do selo Recomendado e da nota
 * da IA: o atalho fecha cada trecho no voo que o agente pôs à frente, na tarifa
 * mais barata dele (o default da escada, o preço do card). Contorno e não sólido: o
 * sólido desta tela é o `Selecionar` da escada, visível ao mesmo tempo, e dois
 * sólidos dizendo "confirmar" em escopos diferentes (um trecho / o roteiro) obrigam a pessoa a descobrir qual fecha o
 * quê. Além disso o atalho é um DESVIO: sólido, ele leria como "não precisa ler os
 * cards", e o que ele gasta é a confiança que o trio produziu.
 *
 * `compact` é o rótulo curto do celular ("Tarifas mais baratas (3)"); o nome acessível
 * segue o longo, com o verbo, e contém o texto visível.
 */
function CheapestShortcut({
  pending,
  undoable,
  busy,
  onAccept,
  onUndo,
  compact,
}: SkirtShortcut & { readonly compact: boolean }) {
  const { t } = useGenUiI18n();
  if (undoable) {
    return (
      <button
        type="button"
        className="fui-btn fui-btn-outline fui-btn-lg fls-undo"
        onClick={onUndo}
      >
        {t('flightMultiOffers.undo')}
      </button>
    );
  }
  const n = pending === 1 ? '_one' : '_other';
  const label = t(`flightMultiOffers.acceptCheapestFares${n}`, { count: pending });
  return (
    <button
      type="button"
      className="fui-btn fui-btn-outline fui-btn-lg fls-accept"
      disabled={busy}
      onClick={onAccept}
      {...(compact ? { 'aria-label': label } : {})}
    >
      <SparkleIcon size={13} weight="fill" aria-hidden />
      {compact ? t(`flightMultiOffers.acceptCheapestFaresShort${n}`, { count: pending }) : label}
    </button>
  );
}

/**
 * A porta para a lista completa — na SAIA do card (UX Review Pacote 2, M3).
 * O clique captura o `active` DESTE render — o explorer abre o roteiro inteiro
 * na perna em que a pessoa está. Funil + seta, a mesma composição do CTA do
 * card binário pós-F8; outline, porque o sólido da saia é o Confirmar. No
 * celular o rótulo encurta ("Todos os voos") e o nome acessível segue o longo.
 */
function ExploreLegCta({ active, onOpen, compact }: SkirtExplore & { readonly compact: boolean }) {
  const { t } = useGenUiI18n();
  const label = t('flightExplorer.legCta');
  return (
    <button
      type="button"
      // Na linha larga a porta ancora à ESQUERDA e empurra o resto à direita.
      className={`fui-btn fui-btn-outline fui-btn-lg fmo-explore${compact ? '' : ' mr-auto'}`}
      onClick={() => {
        onOpen(active);
      }}
      {...(compact ? { 'aria-label': label } : {})}
    >
      <FunnelIcon size={15} aria-hidden />
      {compact ? t('flightExplorer.legCtaShort') : label}
      <ArrowRightIcon size={15} aria-hidden />
    </button>
  );
}

/**
 * **A saia única do roteiro (UX Review Pacote 2, M3).** As três decisões do card
 * moram no rodapé: a porta para a lista completa, o atalho das tarifas
 * mais baratas/Desfazer (os dois em contorno) e o `Confirmar` (o único sólido).
 * Antes o atalho vivia no placar e a porta embaixo da lista — três interações em
 * três cantos; o review pediu a confirmação e as ações do roteiro num lugar só.
 *
 * Duas arrumações, pela largura da PRÓPRIA saia (`useOfferLayout`, a régua `sm`
 * das ofertas do card):
 *  - **larga** — uma linha: a porta à esquerda, atalho e Confirmar juntos à
 *    direita, a ordem do desenho do review;
 *  - **estreita** (celular, Trip aberta) — os três não cabem numa linha (o
 *    Confirmar saía da tela). Os contornos dividem a primeira linha, atalho antes
 *    da porta, com rótulos curtos; sem atalho a porta ocupa a linha toda. O
 *    Confirmar desce para a linha de baixo, inteiro (decisão do design, out/2026).
 *    Medida em JS e não container query porque a ORDEM e os rótulos mudam, não
 *    só o estilo.
 *
 * O Confirmar segue presente desde o primeiro trecho e `disabled` até completar —
 * não some. O botão travado ensina o alvo e ganha evidência ao destravar; aparecer
 * do nada no último pick esconde o alvo justamente enquanto a pessoa decide.
 * `Confirmar` e não `Ir para o checkout`: o clique não navega — despacha os N
 * picks e CONGELA o card; quem decide o que vem depois é o turno do servidor.
 *
 * Linha nua, sem `CardFooter`: a banda muted do `.fui-foot` é chrome de DENTRO de
 * um `.fui-card` (clipada ao raio), e este card renderiza solto no fluxo do chat,
 * como o binário — a faixa cinza aparecia crua e nenhum outro card de voo a tem.
 */
export function ItinerarySkirt({
  explore,
  shortcut,
  complete,
  onConfirm,
}: Readonly<{
  explore?: SkirtExplore;
  shortcut?: SkirtShortcut;
  complete: boolean;
  onConfirm: () => void;
}>) {
  const { t } = useGenUiI18n();
  const { ref, layout } = useOfferLayout<HTMLDivElement>();
  const compact = layout === 'stack';
  const exploreCta = explore && <ExploreLegCta {...explore} compact={compact} />;
  // Sem lote a desfazer e sem trecho pendente o atalho não tem o que dizer.
  const shortcutCta = shortcut && (shortcut.undoable || shortcut.pending > 0) && (
    <CheapestShortcut {...shortcut} compact={compact} />
  );
  const confirm = (
    <button
      type="button"
      className="fui-btn fui-btn-primary fui-btn-lg"
      disabled={!complete}
      onClick={onConfirm}
    >
      {t('flightMultiOffers.confirmPick')}
    </button>
  );
  if (compact) {
    return (
      <div ref={ref} className="fmo-skirt mt-3 grid gap-2">
        {(exploreCta || shortcutCta) && (
          <div className="flex gap-2 *:min-w-0 *:flex-1">
            {shortcutCta}
            {exploreCta}
          </div>
        )}
        {confirm}
      </div>
    );
  }
  return (
    <div ref={ref} className="fui-actions fmo-skirt mt-3">
      {exploreCta}
      {shortcutCta}
      {confirm}
    </div>
  );
}
